import { useState, useEffect, MutableRefObject } from 'react'

import { useScheduleEvents } from 'src/Hooks/useScheduleEvents'
import { useCurrency } from 'src/Hooks/useCurrency'
import { CampaignPlatformTypeEnum } from 'src/Types/campaigns'

interface UseCampaignPrice {
  valueSlider: number
  setValueSlider: (value: number) => void
  currencySymbol: string
  rate: number
  onUserChangedSlider: () => void
}

export const ONE_WEEK_CAMPAIGN = 7
export const TWO_WEEKS_CAMPAIGN = 14
export const ONE_MONTH_CAMPAIGN = 30
export const MIN_BUDGET = 5
export const MAX_BUDGET = 200
export const MIN_REACH_COEFFICIENT_PLAYLIST = 300
export const MAX_REACH_COEFFICIENT_PLAYLIST = 870
export const MIN_FOLLOWERS_COEFFICIENT_PLAYLIST = 3
export const MAX_FOLLOWERS_COEFFICIENT_PLAYLIST = 6
export const MIN_REACH_COEFFICIENT_TRACK = 200
export const MAX_REACH_COEFFICIENT_TRACK = 500
export const MIN_FOLLOWERS_COEFFICIENT_TRACK = 1
export const MAX_FOLLOWERS_COEFFICIENT_TRACK = 3

export const useCampaignPrice = (
  sliderTouchedRef: MutableRefObject<boolean>,
  savedDailyBudget?: number,
): Omit<UseCampaignPrice, 'onUserChangedSlider'> => {
  const { handleBudgetEvents } = useScheduleEvents()
  const { currency } = useCurrency(CampaignPlatformTypeEnum.META)

  const [selectedValue, setValueSlider] = useState<number | null>(null)

  const valueSlider = selectedValue ?? savedDailyBudget ?? MIN_BUDGET

  useEffect(() => {
    if (!sliderTouchedRef.current) {
      return
    }

    handleBudgetEvents({
      platformName: CampaignPlatformTypeEnum.META,
      resultPrice: valueSlider,
    })
  }, [handleBudgetEvents, sliderTouchedRef, valueSlider])

  return {
    setValueSlider,
    valueSlider,
    currencySymbol: currency.currencySymbol,
    rate: currency.currencyRate,
  }
}

export const getEstimatedRange = (
  budget: number,
  multiplierMin: number,
  multiplierMax: number,
): [number, number] => [
  Math.round(budget * multiplierMin),
  Math.round(budget * multiplierMax),
]

export const getStrategyRanges = (
  strategy: string | undefined,
  budget: number,
): { reachRange: [number, number]; followersRange: [number, number] } => {
  if (strategy === 'playlist') {
    return {
      reachRange: getEstimatedRange(
        budget,
        MIN_REACH_COEFFICIENT_PLAYLIST,
        MAX_REACH_COEFFICIENT_PLAYLIST,
      ),
      followersRange: getEstimatedRange(
        budget,
        MIN_FOLLOWERS_COEFFICIENT_PLAYLIST,
        MAX_FOLLOWERS_COEFFICIENT_PLAYLIST,
      ),
    }
  }
  if (strategy === 'track') {
    return {
      reachRange: getEstimatedRange(
        budget,
        MIN_REACH_COEFFICIENT_TRACK,
        MAX_REACH_COEFFICIENT_TRACK,
      ),
      followersRange: getEstimatedRange(
        budget,
        MIN_FOLLOWERS_COEFFICIENT_TRACK,
        MAX_FOLLOWERS_COEFFICIENT_TRACK,
      ),
    }
  }
  return { reachRange: [0, 0], followersRange: [0, 0] }
}

export const durationButtons = [
  { value: ONE_WEEK_CAMPAIGN, text: '7 days' },
  {
    value: TWO_WEEKS_CAMPAIGN,
    text: '14 days',
    labelText: 'Popular',
  },
  { value: ONE_MONTH_CAMPAIGN, text: '30 days' },
]

export const getInitialDays = (savedDays?: number | null): number =>
  durationButtons.find(({ value }) => value === savedDays)?.value ??
  TWO_WEEKS_CAMPAIGN
